import { useMergeRefs } from '@floating-ui/react'
import { ReactNode, useEffect, useRef } from 'react'

import { useClickOutside } from '../../hooks/useClickOutside'
import { useVirtualizeRows } from '../../hooks/useVirtualizeRows'
import { BodyRow, CellHeight, cellHeightStyle, Loader, ModalRender, Rows, TableBody } from './components'
import { useDataGridContext } from './hooks/useDataGridContext'
import { useRecalculatePositionOnNavigation } from './hooks/useRecalculatePositionOnNavigation'
import { Cell, Id, RowType } from './types'
import { isDefined, toCellId } from './utils'

export type DataGridProps<TRow extends { id: Id }, TCol extends { id: Id }> = {
    cols: TCol[]
    rows: TRow[]

    body: {
        rowHeader?: (row: TRow) => ReactNode
        rowClassName?: (row: TRow) => string

        cellRender: (row: TRow, col: TCol, isSelected: boolean) => ReactNode
        cellClassName?: (row: TRow, col: TCol) => string
    }

    headers?: (RowType<TCol> | null)[]
    footers?: (RowType<TCol> | null)[]
    modalContentRender?: ModalRender
    id?: string
    onClick?: () => void
    onSelectedCellsChange?: (cells: Cell[]) => void
    hideHeading?: boolean
    fullWidthCells?: boolean
    'data-test'?: string
    cellDataTest?: string
    firstColumnWidth?: number
    className?: string
    isLoading?: boolean
    allowClickOutside?: boolean
    disableCellHover?: boolean
    borderless?: boolean

    /**
     * If set to valid value, datagrid will add virtualization to the body.
     * This will improve performance for large datasets but requires
     * predefined cell height.
     * */
    optimizeRendering?: { cellHeight?: CellHeight }

    /**
     * If true, disables all interactivity (pointer events, keyboard, etc) for the table.
     */
    disableInteractivity?: boolean
}

export function DataGrid<TRow extends { id: Id }, TCol extends { id: Id }>({
    id,
    cols,
    rows,
    body,
    headers,
    footers,
    cellDataTest,
    modalContentRender,
    onClick,
    'data-test': dataTest,
    className,
    firstColumnWidth = 64,
    hideHeading,
    fullWidthCells,
    isLoading,
    allowClickOutside = false,
    optimizeRendering,
    disableCellHover = false,
    disableInteractivity = false,
    borderless = false,
}: DataGridProps<TRow, TCol>) {
    const isCellFullWidth = fullWidthCells ? 'minmax(28px, 1fr)' : 'minmax(28px, 190px)'
    const gridTemplateColumns = !hideHeading ? `${firstColumnWidth}px repeat(${cols.length}, ${isCellFullWidth})` : `repeat(${cols.length}, minmax(28px, 1fr))`

    const { cursor, setDefault, blockKeyInput } = useDataGridContext()

    const dataGridRef = useRef<HTMLDivElement>(null)
    const headerRef = useRef<HTMLDivElement>(null)
    const bodyRef = useRef<HTMLDivElement>(null)
    const footerRef = useRef<HTMLDivElement>(null)
    const previousFocusedCursorIdRef = useRef<string | undefined>(undefined)

    useRecalculatePositionOnNavigation({ firstColumnWidth, dataGridRef, cursor, headerRef, footerRef })

    const rowHeight = cellHeightStyle[optimizeRendering?.cellHeight ?? 'tall']
    // Range is already clamped to the row count by the hook; nominal row height
    // is the exact pitch here since DataGrid borders sit on fixed-height cells.
    const {
        start: visibleStart,
        end: visibleEnd,
        ref: virtualizerRef,
    } = useVirtualizeRows({
        rowCount: rows.length,
        nominalRowHeight: rowHeight,
        overscan: 2,
        enabled: optimizeRendering !== undefined,
    })
    const gridRef = useMergeRefs([dataGridRef, virtualizerRef])

    // should expose with callBack
    const handleClickOutside = () => {
        if (blockKeyInput || allowClickOutside) return
        setDefault()
    }

    useClickOutside(bodyRef, handleClickOutside)

    useEffect(() => {
        const cursorId = cursor ? toCellId(cursor) : undefined

        if (!cursorId) {
            previousFocusedCursorIdRef.current = undefined
            return
        }

        if (blockKeyInput || previousFocusedCursorIdRef.current === cursorId) return

        const cursorCellEl = document.getElementById(cursorId)
        if (!cursorCellEl) return

        previousFocusedCursorIdRef.current = cursorId
        cursorCellEl.focus({ preventScroll: true })
    }, [cursor, blockKeyInput])

    return (
        <div
            ref={gridRef}
            id={id}
            data-test={dataTest}
            onClick={disableInteractivity ? undefined : onClick}
            className={`thin-scroll isolate grid h-full max-h-full max-w-full select-none overflow-auto rounded-lg ${borderless ? '' : 'border border-subtle'} bg-surface-base ${className}`}
            style={{ gridTemplateRows: 'auto 1fr auto' }}
        >
            {!isLoading ? (
                <>
                    <div ref={headerRef} className="sticky top-0 z-priority grid whitespace-nowrap border-b-2">
                        {headers
                            ?.filter(isDefined)
                            .map(({ key, ...header }) => <Rows key={key} cols={cols} gridTemplateColumns={gridTemplateColumns} {...header} />)}
                    </div>

                    <div ref={bodyRef} className="isolate grid h-full auto-rows-min">
                        {optimizeRendering ? (
                            <TableBody rows={rows} rowHeight={rowHeight} start={visibleStart} end={visibleEnd}>
                                {(row, rowHeight) => (
                                    <BodyRow
                                        key={row.id}
                                        cellDataTest={cellDataTest}
                                        gridTemplateColumns={gridTemplateColumns}
                                        row={row}
                                        height={rowHeight}
                                        cols={cols}
                                        {...body}
                                        modalContentRender={modalContentRender}
                                        disableCellHover={disableCellHover}
                                    >
                                        {body.cellRender}
                                    </BodyRow>
                                )}
                            </TableBody>
                        ) : (
                            <>
                                {rows.map(row => (
                                    <BodyRow
                                        key={row.id}
                                        gridTemplateColumns={gridTemplateColumns}
                                        row={row}
                                        cellDataTest={cellDataTest}
                                        cols={cols}
                                        {...body}
                                        disableCellHover={disableCellHover}
                                        modalContentRender={modalContentRender}
                                    >
                                        {body.cellRender}
                                    </BodyRow>
                                ))}
                            </>
                        )}
                    </div>

                    <div ref={footerRef} className={`sticky bottom-0 grid whitespace-nowrap ${Number(footers?.length) > 0 ? 'border-t-2' : ''}`}>
                        {footers
                            ?.filter(isDefined)
                            .map(({ key, ...footer }) => <Rows key={key} cols={cols} gridTemplateColumns={gridTemplateColumns} {...footer} />)}
                    </div>
                </>
            ) : (
                <Loader cols={cols.length} />
            )}
        </div>
    )
}
